import { createSlice } from '@reduxjs/toolkit';
// utils
import { auth, config } from 'src/backend';
import _noop from 'lodash/noop';
import * as Sentry from '@sentry/react';
import { firebaseSignOut } from 'src/adapters/FirestoreAdapter';
import {
  clearAdminImpersonation,
  getAdminImpersonation,
} from 'src/utils/adminImpersonation';
// ----------------------------------------------------------------------

// Freshcue donor access derived from GET /user/me. Permissions use this
// boolean via `orHasOrgFlag: 'freshcue_donor_enabled'`.
export const withFreshcueDonorFlag = (user) =>
  user
    ? {
        ...user,
        freshcue_donor_enabled: Number(user.freshcue_donor_program_count) > 0,
      }
    : user;

const initialState = {
  isLoading: true,
  isAuthenticated: false,
  user: {},
  impersonation: null,
  isOrgModerationEnabled: false,
  isEmailSuppressed: false,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // INITIALISE
    getInitialize(state, action) {
      state.isLoading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = withFreshcueDonorFlag(action.payload.user);
      state.impersonation = action.payload.impersonation ?? null;
      state.isOrgModerationEnabled = action.payload.isOrgModerationEnabled;
      state.isEmailSuppressed = action.payload.isEmailSuppressed;
    },

    // LOGIN
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = withFreshcueDonorFlag(action.payload.user);
      state.impersonation = null;
    },

    // REGISTER
    registerSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = withFreshcueDonorFlag(action.payload.user);
      state.impersonation = null;
    },

    // LOGOUT
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.impersonation = null;
    },

    setUserData(state, action) {
      state.user = withFreshcueDonorFlag(action.payload.user);
    },

    setImpersonation(state, action) {
      state.impersonation = action.payload;
    },

    setSuppressionStatus(state, action) {
      state.isEmailSuppressed = action.payload.isEmailSuppressed;
    },
  },
});

// Reducer
export default slice.reducer;

export const setUserData = slice.actions.setUserData;
export const setSuppressionStatus = slice.actions.setSuppressionStatus;
export const setImpersonation = slice.actions.setImpersonation;

// ----------------------------------------------------------------------

export function updateUserData() {
  return async (dispatch) => {
    const user = await auth.getUserData();
    dispatch(slice.actions.setUserData({ user }));
  };
}

// ----------------------------------------------------------------------

export function setSession(
  accessToken,
  refreshToken,
  { noRevoke = true } = {}
) {
  // Revoke the old refresh token if necessary
  const oldRefreshToken = localStorage.getItem('refresh-token');
  if (oldRefreshToken && refreshToken !== oldRefreshToken && !noRevoke) {
    auth.revokeRefreshToken(oldRefreshToken).catch((err) => {
      console.error(err);
    });
  }

  if (accessToken && refreshToken) {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh-token', refreshToken);
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh-token');
  }
}

// ----------------------------------------------------------------------

// Account-blocked errors (license lockout / declined org) are not auth
// failures: the credentials and session are valid, the org is blocked.
// Flows that hit one keep the session so AccountBlockedScreen can offer
// switching into the user's other organizations.
const isAccountBlockedError = (e) =>
  e?.code === 'ACCOUNT_LICENSE_LOCKED' || e?.declinedApproval === true;

// Minimal user reconstructed from the JWT for blocked-org sessions where
// /me itself is blocked; enough for routing guards and the blocked screen.
const parseJwtUser = () => {
  try {
    const token = window.localStorage.getItem('token');
    const segment = token?.split('.')[1];
    if (!segment) return null;
    // JWT payloads are base64url — convert before atob, which only
    // accepts standard base64 and would throw on '-' / '_'.
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return {
      id: payload.id,
      account_type: payload.role,
      team_id: payload.team_id,
      is_non_profit: payload.is_non_profit,
      is_executive: payload.is_executive,
    };
  } catch (e) {
    return null;
  }
};

export function login({ email, password, teamId, then, except }) {
  return async (dispatch) => {
    try {
      const body = await auth.login(email, password, teamId);

      const { token, refreshToken, ...user } = body;
      clearAdminImpersonation();
      setSession(token, refreshToken);

      try {
        const suppressionStatusRes = await auth.getSuppressionStatus(email);
        dispatch(
          setSuppressionStatus({
            isEmailSuppressed: suppressionStatusRes.suppressionStatus,
          })
        );
      } catch (suppressionError) {
        // Non-critical, and it 403s when the chosen org is blocked — login
        // must still complete so the blocked screen (with its org switcher)
        // can render instead of a dead-end login error.
        console.error(suppressionError);
      }
      Sentry.setUser({ id: user.id, teamId: user.team_id });
      dispatch(
        slice.actions.loginSuccess({
          user,
        })
      );
      then();
    } catch (e) {
      except(e);
    }
  };
}

// ----------------------------------------------------------------------

/**
 * @param {Function} [except] Called with the error first; return true when it
 *   was handled (e.g. the email-code step) to skip the error toast.
 */
/**
 * Whether a successful signup starts a dashboard session right away: not
 * while the org awaits moderation, nor when the caller opted out.
 */
export const startsSessionAfterRegister = ({
  isOrgModerationEnabled,
  skipSession,
}) => !isOrgModerationEnabled && !skipSession;

/**
 * @param {object} [options]
 * @param {boolean} [options.skipSession] keep the new account signed out on
 *   this site (e.g. a Freshcue signup that continues in the app): `then`
 *   gets the response either way and no dashboard session is started.
 */
export function register(
  userData,
  enqueueSnackbar,
  setData,
  then = _noop,
  except = undefined,
  { skipSession = false } = {}
) {
  return async (dispatch) => {
    let body;
    try {
      body = await auth.register(userData);
    } catch (err) {
      if (except?.(err)) return;
      enqueueSnackbar(err.message, { variant: 'error' });
      return;
    }
    setData({});
    const configRes = await config.getSystemConfig();
    if (
      !startsSessionAfterRegister({
        isOrgModerationEnabled: !!configRes?.is_org_moderation_enabled,
        skipSession,
      })
    ) {
      then(body);
    } else {
      const { token, refreshToken, ...user } = body;
      clearAdminImpersonation();
      setSession(token, refreshToken);
      Sentry.setUser({ id: user.id, teamId: user.team_id });
      dispatch(slice.actions.registerSuccess({ user }));
      then();
    }
  };
}

// ----------------------------------------------------------------------

export function logout() {
  return async (dispatch) => {
    clearAdminImpersonation();
    setSession(null, null);
    dispatch(slice.actions.logoutSuccess());
  };
}

// ----------------------------------------------------------------------

export function getInitialize() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    let configRes;
    try {
      configRes = await config.getSystemConfig();
      const accessToken = window.localStorage.getItem('token');
      const refreshToken = window.localStorage.getItem('refresh-token');

      if (accessToken && refreshToken) {
        setSession(accessToken, refreshToken);

        const userData = await auth.getUserData();
        const suppressionStatusRes = await auth.getSuppressionStatus(
          userData.email
        );
        Sentry.setUser({ id: userData.id, teamId: userData.team_id });
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: true,
            user: userData,
            impersonation: getAdminImpersonation(),
            isOrgModerationEnabled: !!configRes?.is_org_moderation_enabled,
            isEmailSuppressed: suppressionStatusRes.suppressionStatus,
          })
        );
      } else {
        Sentry.setUser(null);
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: false,
            user: null,
            impersonation: null,
            isOrgModerationEnabled: !!configRes?.is_org_moderation_enabled,
          })
        );
      }
    } catch (error) {
      if (isAccountBlockedError(error)) {
        // Valid session, blocked org (license lockout / declined). Keep the
        // session authenticated so the router doesn't bounce to login and
        // AccountBlockedScreen can offer switching to another organization.
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: true,
            // is_partial_session marks a JWT-reconstructed user (no /me
            // data) so UI effects like the name-prompt modal can skip it.
            user: { ...(parseJwtUser() || {}), is_partial_session: true },
            impersonation: getAdminImpersonation(),
            isOrgModerationEnabled: !!configRes?.is_org_moderation_enabled,
            isEmailSuppressed: false,
          })
        );
        return;
      }
      firebaseSignOut();
      console.error('token error', { error });
      window.localStorage.removeItem('token');
      Sentry.setUser(null);
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null,
          impersonation: null,
        })
      );
    }
  };
}
